// src/pages/kpis/KpiCustoManutencaoPage.jsx
import React, { useEffect, useMemo, useState } from "react";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";

import PeriodFilter from "../../components/Kpis/PeriodFilter";

import { db } from "../../config/firebase";
import { collection, getDocs, limit, query, Timestamp, where } from "firebase/firestore";

function tsToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "object" && typeof ts.toDate === "function") return ts.toDate();
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
  return null;
}


function moneyBRL(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export default function KpiCustoManutencaoPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [periodo, setPeriodo] = useState({ preset: "30D", start: null, end: null });

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [resumo, setResumo] = useState({ total: 0, itens: 0 });
  const [porOrigem, setPorOrigem] = useState([]);
  const [topMaquinas, setTopMaquinas] = useState([]);

  const canLoad = useMemo(() => !!usuario?.grupoId, [usuario]);

  async function carregar() {
    if (!canLoad) return;
    if (!periodo?.start || !periodo?.end) return;

    setCarregando(true);
    setErro("");

    try {
      const ref = collection(db, "pedidosMateriais");
      const q = query(ref, where("grupoId", "==", usuario.grupoId), limit(5000));

      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const startMs = periodo.start.getTime();
      const endMs = periodo.end.getTime();
      const lista = all
        .filter((p) => {
          const dp = tsToDate(p.dataPedido);
          if (!dp) return false;
          const ms = dp.getTime();
          return ms >= startMs && ms <= endMs;
        })
        .sort((a, b) => (tsToDate(b.dataPedido)?.getTime() || 0) - (tsToDate(a.dataPedido)?.getTime() || 0));

      let total = 0;
      for (const p of lista) {
        total += Number(p.valorTotal) || 0;
      }

      // Por origem
      const mapOrigem = new Map();
      for (const p of lista) {
        const key = p.origem || "(Sem origem)";
        mapOrigem.set(key, (mapOrigem.get(key) || 0) + (Number(p.valorTotal) || 0));
      }
      const origem = Array.from(mapOrigem.entries())
        .map(([origem, valor]) => ({ origem, valor }))
        .sort((a, b) => b.valor - a.valor);

      // Top máquinas
      const mapMaq = new Map();
      for (const p of lista) {
        const key = p.maquinaNome || p.maquinaId || "(Sem máquina)";
        mapMaq.set(key, (mapMaq.get(key) || 0) + (Number(p.valorTotal) || 0));
      }
      const top = Array.from(mapMaq.entries())
        .map(([maquina, valor]) => ({ maquina, valor }))
        .sort((a, b) => b.valor - a.valor)
        .slice(0, 10);

      setResumo({ total, itens: lista.length });
      setPorOrigem(origem);
      setTopMaquinas(top);
    } catch (e) {
      console.error(e);
      setErro("Erro ao carregar custos. Verifique índices/consultas do Firestore.");
      setResumo({ total: 0, itens: 0 });
      setPorOrigem([]);
      setTopMaquinas([]);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (periodo?.start && periodo?.end) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.grupoId, periodo?.start, periodo?.end]);

  const conteudo = (
    <section style={{ width: "100%" }}>
      <div className="pc-card" style={{ width: "100%" }}>
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Custo de Manutenção</h3>
            <p>Base: pedidos de material (coleção pedidosMateriais) no período.</p>
          </div>
        </header>

        <PeriodFilter value={periodo} onApply={(v) => setPeriodo(v)} />

        {erro && (
          <div className="pc-alert pc-alert-error" style={{ marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 12 }}>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Total no período</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{moneyBRL(resumo.total)}</div>
          </div>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Itens (linhas de pedidos)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{resumo.itens}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Por origem</div>
            {carregando ? (
              <div>Carregando...</div>
            ) : porOrigem.length ? (
              <div style={{ overflowX: "auto" }}>
                <table className="pc-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Origem</th>
                      <th style={{ textAlign: "right" }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porOrigem.map((r) => (
                      <tr key={r.origem}>
                        <td>{r.origem}</td>
                        <td style={{ textAlign: "right" }}>{moneyBRL(r.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ opacity: 0.8 }}>Sem dados no período.</div>
            )}
          </div>

          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Top máquinas (por custo)</div>
            {carregando ? (
              <div>Carregando...</div>
            ) : topMaquinas.length ? (
              <div style={{ overflowX: "auto" }}>
                <table className="pc-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Máquina</th>
                      <th style={{ textAlign: "right" }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topMaquinas.map((r) => (
                      <tr key={r.maquina}>
                        <td>{r.maquina}</td>
                        <td style={{ textAlign: "right" }}>{moneyBRL(r.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ opacity: 0.8 }}>Sem dados no período.</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );

  return isMobile ? (
    <LayoutMobile
      tema={tema}
      alterarTema={alterarTema}
      titulo="KPIs — Custo"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
    >
      {conteudo}
    </LayoutMobile>
  ) : (
    <LayoutDesktop tema={tema} titulo="KPIs" subtitulo="Custo Manutenção">
      {conteudo}
    </LayoutDesktop>
  );
}